import { TextFieldProps } from "@shopify/polaris"
import React, { useCallback, useEffect, useState } from "react"

import stores from "@/stores"

import { RuleItem } from "../../utils"
import { formatDisplayPrice, parseRealPrice } from "../../utils/price"
import ProTextField from "./ProTextField"

interface ProPriceTextFieldProps extends Omit<
  TextFieldProps,
  "value" | "onChange" | "autoComplete" | "label"
> {
  value: number | undefined
  rules?: RuleItem[]
  onChange: (value: number | string) => void
  maxValue?: number
  minValue?: number
  label?: any
}

export function ProTextFieldPrice({
  value,
  rules,
  onChange,
  maxValue = 100000,
  minValue = 0,
  ...props
}: ProPriceTextFieldProps) {
  // 使用 useState 管理内部状态
  const [internalValue, setInternalValue] = useState<string>(String(value))
  const [isFocus, setIsFocus] = useState(false)

  useEffect(() => {
    // 如果正在聚焦，不更新内部值
    if (isFocus) return
    // 如果value为undefined，设置内部值为空
    if (value === undefined) {
      setInternalValue("")
    } else {
      setInternalValue(formatDisplayPrice(value))
    }
  }, [value, isFocus])

  // 处理输入变化
  const handleChange = useCallback(
    (input: string) => {
      setInternalValue(input)
      if (input === "") {
        onChange("")
      } else {
        const realPrice = parseRealPrice(input)

        // 如果解析结果是空字符串，直接返回空字符串
        if (realPrice === "") {
          onChange("")
          return
        }

        // 检查最大值和最小值条件
        if (maxValue !== undefined && Number(realPrice) > maxValue) {
          const formatMaxValue = formatDisplayPrice(maxValue)
          setInternalValue(formatMaxValue)
          onChange(formatMaxValue)
          return
        }
        if (minValue !== undefined && Number(realPrice) < minValue) {
          const formatMinValue = formatDisplayPrice(minValue)
          setInternalValue(formatMinValue)
          onChange(formatMinValue)
          return
        }

        if (realPrice !== value) {
          onChange(realPrice)
        }
      }
    },
    [onChange, value, maxValue, minValue],
  )

  // 在失焦时触发 onChange
  const handleBlur = useCallback(
    (e) => {
      // 如果internalValue为空，onChange为空
      const realPrice = parseRealPrice(internalValue)
      // 如果解析结果是空字符串，直接返回空字符串
      if (realPrice === "") {
        setInternalValue("")
        onChange("")
        props.onBlur?.(e)
        return
      }

      // 检查最大值和最小值条件
      if (maxValue !== undefined && Number(realPrice) > maxValue) {
        const formatMaxValue = formatDisplayPrice(maxValue)
        setInternalValue(formatMaxValue)
        onChange(formatMaxValue)
        props.onBlur?.(e)
        setIsFocus(false)
        return
      }
      if (minValue !== undefined && Number(realPrice) < minValue) {
        const formatMinValue = formatDisplayPrice(minValue)
        setInternalValue(formatMinValue)
        onChange(formatMinValue)
        props.onBlur?.(e)
        setIsFocus(false)
        return
      }

      const formatRealPrice = formatDisplayPrice(Number(realPrice))
      setInternalValue(formatRealPrice)
      onChange(formatRealPrice)
      props.onBlur?.(e)
      setIsFocus(false)
    },
    [internalValue, props, maxValue, minValue, onChange],
  )

  return (
    <ProTextField
      {...props}
      rules={rules}
      onFocus={(e) => {
        setIsFocus(true)
        props.onFocus?.(e)
      }}
      value={internalValue}
      onChange={handleChange}
      onBlur={handleBlur}
      maxLength={props.maxLength ?? 24}
      prefix={props.prefix ?? stores.UserInfoStore.data.currency_format ?? "$"}
      placeholder={props.placeholder ?? "0.00"}
    />
  )
}
